import React, { useState, useEffect } from 'react';
import { Tv, Package, Users, Monitor, CreditCard, Wallet, TrendingUp, Activity } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ icon: Icon, label, value, color, sub, onClick }) => (
  <div className="card" onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: '16px', cursor: onClick ? 'pointer' : 'default' }}>
    <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={22} color={color} />
    </div>
    <div>
      <div style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)' }}>{value ?? 0}</div>
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{label}</div>
      {sub && <div style={{ fontSize: '11px', color, marginTop: '2px' }}>{sub}</div>}
    </div>
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    channels: 0, packages: 0, distributors: 0, lcos: 0,
    endusers: 0, activeSubs: 0, expiredSubs: 0, totalBalance: '0.00'
  });
  const [recentSubs, setRecentSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myCode, setMyCode] = useState(null);
  const [myTenantDue, setMyTenantDue] = useState(undefined);
  const isSuperAdmin = user?.role === 'superadmin';

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const canSeeLiveViewers = isSuperAdmin || user?.role === 'distributor' || user?.role === 'lco';
      const results = await Promise.allSettled([
        api.get('/api/users'),
        api.get('/api/subscriptions'),
        api.get('/api/packages'),
        isSuperAdmin ? api.get('/api/channels') : Promise.resolve({ data: { data: [] } }),
        isSuperAdmin ? api.get('/api/wallet/all') : api.get('/api/wallet'),
        canSeeLiveViewers ? api.get('/api/channels/live/viewers') : Promise.resolve({ data: { data: [] } }),
      ]);
      const liveViewers = results[5].status === 'fulfilled' ? results[5].value.data.data || [] : [];

      const allUsers = results[0].status === 'fulfilled' ? results[0].value.data.data || [] : [];
      const allSubs = results[1].status === 'fulfilled' ? results[1].value.data.data || [] : [];
      const allPkgs = results[2].status === 'fulfilled' ? results[2].value.data.data || [] : [];
      const allChannels = results[3].status === 'fulfilled' ? results[3].value.data.data || [] : [];

      let totalBalance = '0.00';
      if (results[4].status === 'fulfilled') {
        if (isSuperAdmin) {
          const wallets = results[4].value.data.data || [];
          totalBalance = wallets.reduce((s, w) => s + parseFloat(w.balance || 0), 0).toFixed(2);
        } else {
          const w = results[4].value.data.data;
          totalBalance = parseFloat(w?.balance || 0).toFixed(2);
        }
      }

      const activeSubs = allSubs.filter(s => s.is_active && new Date(s.end_date) > new Date());
      const expiredSubs = allSubs.filter(s => !s.is_active || new Date(s.end_date) <= new Date());
      const overdueTenants = allUsers.filter(u => u.role === 'distributor' && u.tenant_next_due_date && new Date(u.tenant_next_due_date) < new Date());

      setStats({
        channels: allChannels.length,
        packages: allPkgs.length,
        distributors: allUsers.filter(u => u.role === 'distributor').length,
        lcos: allUsers.filter(u => u.role === 'lco').length,
        endusers: allUsers.filter(u => u.role === 'enduser' && u.has_active_subscription).length,
        activeSubs: new Set(activeSubs.map(s => s.user_id).filter(Boolean)).size || activeSubs.length,
        expiredSubs: new Set(expiredSubs.map(s => s.mac_address).filter(Boolean)).size || expiredSubs.length,
        overdueTenants: overdueTenants.length,
        liveViewers: liveViewers.length,
        totalBalance,
      });

      setRecentSubs(allSubs.slice(0, 10));

      // Fetch operator code for dist/lco
      if (!isSuperAdmin) {
        try {
          const codeRes = await api.get('/api/users/my-code');
          setMyCode(codeRes.data.operator_code);
          if (codeRes.data.role === 'distributor') {
            setMyTenantDue(codeRes.data.tenant_next_due_date);
          }
        } catch {}
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" />Loading dashboard...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">Welcome back — here's what's happening on your platform</div>
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {!isSuperAdmin && myTenantDue !== undefined && (() => {
        const overdue = myTenantDue && new Date(myTenantDue) < new Date();
        if (!myTenantDue && myTenantDue !== null) return null;
        return (
          <div style={{
            background: overdue ? 'rgba(255,71,87,0.1)' : 'rgba(245,166,35,0.08)',
            border: `1px solid ${overdue ? '#FF4757' : '#F5A623'}`,
            borderRadius: '12px', padding: '16px 20px', marginBottom: '20px',
            display: 'flex', alignItems: 'center', gap: '16px'
          }}>
            <div style={{ fontSize: '24px' }}>{overdue ? '⚠️' : '💳'}</div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Platform Subscription</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: overdue ? '#FF4757' : '#F5A623' }}>
                {myTenantDue
                  ? (overdue ? `Overdue since ${new Date(myTenantDue).toLocaleDateString('en-IN')}` : `Paid until ${new Date(myTenantDue).toLocaleDateString('en-IN')}`)
                  : 'Not yet tracked - contact NovaCast'}
              </div>
              {overdue && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>Your subscribers' service is currently suspended. Please contact NovaCast to resolve payment.</div>}
            </div>
          </div>
        );
      })()}
      {!isSuperAdmin && myCode && (
        <div style={{
          background: 'rgba(79,110,247,0.1)', border: '1px solid var(--accent-blue)',
          borderRadius: '12px', padding: '16px 20px', marginBottom: '20px',
          display: 'flex', alignItems: 'center', gap: '16px'
        }}>
          <div style={{ fontSize: '24px' }}>🔑</div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Your Operator Code</div>
            <div style={{ fontSize: '28px', fontWeight: '900', color: 'var(--accent-blue)', letterSpacing: '8px' }}>{myCode}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>Share this code with your subscribers to connect their devices</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        {isSuperAdmin && <StatCard icon={Tv} label="Total Channels" value={stats.channels} color="#4f6ef7" />}
        <StatCard icon={Package} label="Total Packages" value={stats.packages} color="#00d4aa" />
        {isSuperAdmin && <StatCard icon={Users} label="Distributors" value={stats.distributors} color="#f7a24f" />}
        {isSuperAdmin && <StatCard icon={CreditCard} label="Tenants Overdue" value={stats.overdueTenants} color="#FF4757" onClick={() => navigate('/users')} />}
        {(isSuperAdmin || user?.role === 'distributor' || user?.role === 'lco') && <StatCard icon={Activity} label="Watching Now" value={stats.liveViewers} color="#00d4aa" sub="Live" onClick={() => navigate('/live-viewers')} />}
        <StatCard icon={Users} label="LCOs" value={stats.lcos} color="#f7d44f" />
        <StatCard icon={Users} label="End Users" value={stats.endusers} color="#a78bfa" />
        <StatCard icon={CreditCard} label="Active Subscriptions" value={stats.activeSubs} color="#00d4aa" sub="Currently active" />
        <StatCard icon={Activity} label="Expired" value={stats.expiredSubs} color="#f75f5f" onClick={() => navigate('/subscriptions?filter=expired')} />
        <StatCard icon={Wallet} label={isSuperAdmin ? "Total Wallet Balance" : "My Balance"} value={`₹${stats.totalBalance}`} color="#4f6ef7" />
      </div>

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <TrendingUp size={18} color="var(--accent-blue)" />
          <span style={{ fontWeight: '600', fontSize: '15px' }}>Recent Activations</span>
        </div>
        {recentSubs.length === 0 ? (
          <div className="empty-state">No activations yet</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>MAC Address</th>
                <th>Package</th>
                <th>Expiry</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentSubs.map(s => (
                <tr key={s.id}>
                  <td>{s.full_name || s.username || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{s.mac_address || '—'}</td>
                  <td>{s.package_name || '—'}</td>
                  <td style={{ fontSize: '13px' }}>{s.end_date ? new Date(s.end_date).toLocaleDateString('en-IN') : '—'}</td>
                  <td>
                    <span className={`badge ${s.is_active && new Date(s.end_date) > new Date() ? 'badge-success' : 'badge-danger'}`}>
                      {s.is_active && new Date(s.end_date) > new Date() ? 'Active' : 'Expired'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
